'use client'

import { Children, type ReactNode, useMemo, useState } from 'react'

import IconChevronDownSm from '@/images/icon-chevron-down-sm'
import { tw } from '@/lib/utils'

const TAGS_TO_SHOW_INITIALLY = 2

interface AccessoryTagsExpandableProps {
  totalCount: number
  moreLabel: string
  lessLabel: string
  children: ReactNode
}

export const AccessoryTagsExpandable = ({
  totalCount,
  moreLabel,
  lessLabel,
  children,
}: AccessoryTagsExpandableProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const items = Children.toArray(children)
  const hasOverflow = totalCount > TAGS_TO_SHOW_INITIALLY
  const visible = isExpanded || !hasOverflow ? items : items.slice(0, TAGS_TO_SHOW_INITIALLY)
  const hiddenCount = totalCount - TAGS_TO_SHOW_INITIALLY

  return (
    <ul className="mt-3 flex flex-wrap gap-1.5">
      {visible}
      {hasOverflow && (
        <li>
          <button
            type="button"
            data-tracking="ignore"
            onClick={() => setIsExpanded((v) => !v)}
            aria-expanded={isExpanded}
            data-ht-disclosure-trigger="read-more"
            className="text-lapis bg-lapis/[0.08] hover:bg-lapis/15 flex cursor-pointer items-center gap-1 rounded-full px-4 py-2 text-[14px] font-medium -tracking-[0.42px] transition-colors lg:text-sm"
          >
            {isExpanded ? lessLabel : moreLabel.replace('{{count}}', String(hiddenCount))}
          </button>
        </li>
      )}
    </ul>
  )
}

interface AccessoryAboutExpandableProps {
  about: string
  readMoreLabel: string
  readLessLabel: string
}

export const AccessoryAboutExpandable = ({ about, readMoreLabel, readLessLabel }: AccessoryAboutExpandableProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const paragraphs = useMemo(
    () =>
      about
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean),
    [about]
  )

  if (!paragraphs.length) return null

  const hasMore = paragraphs.length > 1 || paragraphs[0].length > 240

  return (
    <div className="mt-6 text-base leading-relaxed -tracking-[0.48px] opacity-80">
      {isExpanded ? (
        <div className="space-y-4">
          {paragraphs.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      ) : (
        <p className="line-clamp-4">{paragraphs[0]}</p>
      )}
      {hasMore && (
        <button
          type="button"
          data-tracking="ignore"
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}
          data-ht-disclosure-trigger="read-more"
          className="text-lapis mt-3 inline-flex cursor-pointer items-center gap-1 text-sm font-medium hover:underline"
        >
          {isExpanded ? readLessLabel : readMoreLabel}
          <IconChevronDownSm className={tw('transition-transform duration-200', isExpanded && 'rotate-180')} />
        </button>
      )}
    </div>
  )
}
